import type {
  BillingCycleSchema,
  PaymentMethodSchema,
  SaleOriginSchema,
} from '@control-center/shared';
import { capBillingDay, closedPeriodEnd, formatDayBR, parseLocalDate } from '@control-center/shared';

import { Field, Input, Select, Textarea } from '../../../components/clients/form-primitives';
import { PillButton } from '../../../components/clients/PillButton';
import type { WizardData } from './Wizard';

interface Step3DetailsProps {
  data: WizardData;
  onPatch: (patch: Partial<WizardData>) => void;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
}

const BILLING_OPTIONS: { value: BillingCycleSchema; label: string }[] = [
  { value: 'mensal', label: 'Mensal' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'pagamento_unico', label: 'Pagamento único' },
];

// BOLETO fora da lista (regra do dono, 2026-08-19): boleto não debita sozinho,
// então assinatura nenhuma renova com ele. Ficava aqui como opção oferecida ao
// operador — escolher um método que o backend agora recusa.
const PAYMENT_OPTIONS: { value: PaymentMethodSchema; label: string }[] = [
  { value: 'cartao', label: 'Cartão' },
  { value: 'pix', label: 'Pix' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'cobranca_manual', label: 'Cobrança manual' },
];

const SALE_ORIGIN_OPTIONS: { value: SaleOriginSchema; label: string }[] = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'referral', label: 'Indicação' },
  { value: 'self_service', label: 'Self-service' },
  { value: 'migration', label: 'Migração' },
];

function computeBillingDay(startDateYmd: string, cycle: BillingCycleSchema): number | null {
  if (cycle === 'pagamento_unico') return null;
  try {
    const d = parseLocalDate(startDateYmd);
    // Mesmo cap do backend/banco — o preview não pode prometer dia 31.
    return capBillingDay(d.getDate());
  } catch {
    return null;
  }
}

/**
 * Sem renovação = período fechado: não gera cobrança e termina sozinha. A data
 * vem do MESMO cálculo que a API grava (closedPeriodEnd, shared).
 */
export function closedEndLabel(data: Pick<WizardData, 'start_date' | 'billing_cycle'>): string {
  const end = data.start_date ? closedPeriodEnd(data.start_date, data.billing_cycle) : null;
  return end
    ? `Sem cobrança: a assinatura termina sozinha em ${formatDayBR(end)} e o acesso é retirado.`
    : 'Sem cobrança automática.';
}

export function Step3Details({
  data,
  onPatch,
  onBack,
  onNext,
  onCancel,
}: Step3DetailsProps): JSX.Element {
  const startDateValid = /^\d{4}-\d{2}-\d{2}$/.test(data.start_date);
  const billingDayPreview = computeBillingDay(data.start_date, data.billing_cycle);
  const canAdvance = startDateValid;

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h2 className="m-0 text-[15px] font-semibold text-ink">Detalhes da assinatura</h2>
        <p className="text-[12px] text-muted">
          Data de início, ciclo de cobrança, pagamento e comissão.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Data de início" required>
          {({ inputId }) => (
            <Input
              id={inputId}
              type="date"
              value={data.start_date}
              onChange={(e) => onPatch({ start_date: e.target.value })}
            />
          )}
        </Field>

        <Field
          label="Ciclo de cobrança"
          required
          hint={
            billingDayPreview != null
              ? `Dia de cobrança: ${billingDayPreview} (calculado da data de início).`
              : 'Cobrança única — sem dia de cobrança recorrente.'
          }
        >
          {({ inputId }) => (
            <Select
              id={inputId}
              value={data.billing_cycle}
              onChange={(e) => onPatch({ billing_cycle: e.target.value as BillingCycleSchema })}
            >
              {BILLING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Forma de pagamento" hint="Opcional — define no Asaas depois.">
          {({ inputId }) => (
            <Select
              id={inputId}
              value={data.payment_method ?? ''}
              onChange={(e) =>
                onPatch({
                  payment_method:
                    e.target.value === '' ? null : (e.target.value as PaymentMethodSchema),
                })
              }
            >
              <option value="">— sem definir —</option>
              {PAYMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Origem da venda" hint="Opcional — pra atribuição comercial.">
          {({ inputId }) => (
            <Select
              id={inputId}
              value={data.sale_origin ?? ''}
              onChange={(e) =>
                onPatch({
                  sale_origin: e.target.value === '' ? null : (e.target.value as SaleOriginSchema),
                })
              }
            >
              <option value="">— sem definir —</option>
              {SALE_ORIGIN_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <Field
        label="Comissionado por (user ID)"
        hint="UUID do vendedor que fechou a venda. Opcional."
      >
        {({ inputId }) => (
          <Input
            id={inputId}
            type="text"
            placeholder="00000000-0000-4000-8000-000000000000"
            value={data.sold_by_user_id ?? ''}
            onChange={(e) =>
              onPatch({ sold_by_user_id: e.target.value.trim() === '' ? null : e.target.value })
            }
          />
        )}
      </Field>

      <Field label="Observações" hint="Notas internas — vão pra audit log, até 5000 caracteres.">
        {({ inputId }) => (
          <Textarea
            id={inputId}
            rows={3}
            placeholder="Detalhes da venda, condições especiais…"
            value={data.observations}
            onChange={(e) => onPatch({ observations: e.target.value })}
          />
        )}
      </Field>

      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={data.auto_renew}
            onChange={(e) => onPatch({ auto_renew: e.target.checked })}
          />
          <span>Renovação automática (recomendado)</span>
        </label>
        <p className="pl-6 text-[11px] leading-[1.4] text-muted-2">
          {data.auto_renew
            ? 'Cobra todo ciclo pelo Asaas até alguém cancelar.'
            : closedEndLabel(data)}
        </p>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-line pt-4">
        <PillButton type="button" onClick={onBack}>
          ← Voltar
        </PillButton>
        <div className="flex items-center gap-2">
          <PillButton type="button" onClick={onCancel}>
            Cancelar
          </PillButton>
          <PillButton solid type="button" onClick={onNext} disabled={!canAdvance}>
            Avançar →
          </PillButton>
        </div>
      </div>
    </div>
  );
}
